import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, CreditCard, ArrowDownCircle, Server, Plus, BadgeCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead } from "@/components/kit";

export default function Billing() {
  const { guild, guildId, reloadGuild } = useGuild();
  const [plans, setPlans] = useState({});
  const [params, setParams] = useSearchParams();
  const [processing, setProcessing] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  const [dperks, setDperks] = useState(null);
  useEffect(() => { api.get("/me/discord-perks").then((r) => setDperks(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setPolling(false); toast.error("Payment status timed out."); return; }
      attempts++;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") { setPolling(false); toast.success("Payment successful! Plan upgraded 🎉"); reloadGuild(); setParams({}); return; }
        if (data.status === "expired") { setPolling(false); toast.error("Payment session expired."); setParams({}); return; }
        setTimeout(poll, 2000);
      } catch (e) { setPolling(false); toast.error("Could not verify payment."); }
    };
    poll();
    // eslint-disable-next-line
  }, [params.get("session_id")]);

  const upgrade = async (planId) => {
    setProcessing(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, guild_id: guildId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setProcessing(null); }
  };

  const order = { free: 0, premium: 1, max: 2 };
  const current = guild?.plan || "free";
  const [history, setHistory] = useState([]);
  const [maxSub, setMaxSub] = useState(null);
  const [maxBusy, setMaxBusy] = useState(false);
  const [myGuilds, setMyGuilds] = useState([]);
  const [transferTo, setTransferTo] = useState("");
  const loadMax = () => api.get("/billing/max").then((r) => setMaxSub(r.data)).catch(() => {});
  useEffect(() => { if (guildId) api.get(`/guilds/${guildId}/billing/history`).then((r) => setHistory(r.data)).catch(() => {}); }, [guildId, current]);
  useEffect(() => { loadMax(); api.get("/guilds").then((r) => setMyGuilds(r.data)).catch(() => {}); }, [current, guildId]);

  const doTransfer = async () => {
    if (!transferTo) return toast.error("Pick a destination server");
    try {
      await api.post("/billing/transfer", { from_guild_id: guildId, to_guild_id: transferTo });
      toast.success("Plan transferred — this server is now Free"); setTransferTo(""); reloadGuild(); loadMax();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const linkMax = async (gid) => {
    setMaxBusy(true);
    try { await api.post("/billing/max/link", { guild_id: gid }); toast.success("Server linked to your Max plan"); loadMax(); reloadGuild(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setMaxBusy(false); }
  };
  const unlinkMax = async (gid) => {
    setMaxBusy(true);
    try { await api.post("/billing/max/unlink", { guild_id: gid }); toast.success("Server unlinked"); loadMax(); reloadGuild(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setMaxBusy(false); }
  };

  const changePlan = async (planId) => {
    setProcessing(planId);
    try {
      await api.post("/billing/change", { plan_id: planId, guild_id: guildId });
      toast.success(planId === "free" ? "Subscription cancelled — reverted to Free" : `Downgraded to ${plans[planId]?.name}`);
      reloadGuild();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setProcessing(null); }
  };

  return (
    <div>
      <PageHead title="Billing & Plans" desc={`Current plan for ${guild?.name || "this server"}`} />
      {dperks?.integration_enabled && dperks?.has_badge && (
        <div className="bg-zinc-900 border rounded-xl p-6 mb-6" data-testid="discord-perks-card" style={{ borderColor: (dperks.badge_color || "#5865F2") + "66" }}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1 rounded-full" style={{ background: (dperks.badge_color || "#5865F2") + "22", color: dperks.badge_color || "#5865F2" }} data-testid="discord-perks-badge">
                <BadgeCheck className="w-4 h-4" /> {dperks.badge}
              </span>
              <div>
                <h3 className="font-head font-semibold">Discord Subscriber Perks · {dperks.tier_name}</h3>
                <p className="text-xs text-zinc-500">Your active Discord subscription unlocks free channel subscriptions on the streaming site.</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black" data-testid="discord-perks-remaining">{dperks.remaining}<span className="text-sm font-normal text-zinc-500"> / {dperks.allocation} free left</span></div>
              {dperks.period_end && <div className="text-xs text-zinc-600">Resets {new Date(dperks.period_end).toLocaleDateString()}</div>}
            </div>
          </div>
        </div>
      )}
      {dperks?.integration_enabled && !dperks?.has_badge && dperks?.linked === false && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6 text-sm text-zinc-400" data-testid="discord-perks-connect">
          Connect a Discord subscription to unlock a subscriber badge and free channel subscriptions on the streaming site.
        </div>
      )}
      {polling && <div className="mb-6 bg-volt/10 border border-volt/30 rounded-lg p-4 flex items-center gap-3 text-volt"><Loader2 className="w-4 h-4 animate-spin" /> Verifying your payment…</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(plans).map(([id, p]) => {
          const isCurrent = id === current;
          const isUpgrade = order[id] > order[current];
          const isDowngrade = order[id] < order[current];
          return (
            <div key={id} data-testid={`billing-plan-${id}`} className={`rounded-xl p-8 border relative ${id === "premium" ? "border-volt/60 bg-zinc-900" : "border-zinc-800 bg-zinc-900"}`}>
              {isCurrent && <div className="absolute -top-3 right-8 text-xs font-bold uppercase bg-volt text-volt-ink px-3 py-1 rounded-full">Current</div>}
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{p.name}</div>
              <div className="mt-3 font-head text-4xl font-black">£{p.price}<span className="text-base font-normal text-zinc-500">/mo</span></div>
              <ul className="mt-6 space-y-3 min-h-[160px]">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-volt mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
              {isCurrent ? (
                <button disabled className="mt-6 w-full rounded-md px-5 py-2.5 font-semibold bg-zinc-800 text-zinc-500 cursor-not-allowed flex items-center justify-center gap-2">Active plan</button>
              ) : isUpgrade ? (
                <button data-testid={`upgrade-${id}`} disabled={!!processing} onClick={() => upgrade(id)}
                  className="mt-6 w-full rounded-md px-5 py-2.5 font-semibold bg-volt text-volt-ink hover:bg-volt-hover flex items-center justify-center gap-2">
                  {processing === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Upgrade to {p.name}
                </button>
              ) : isDowngrade ? (
                <button data-testid={`downgrade-${id}`} disabled={!!processing} onClick={() => changePlan(id)}
                  className="mt-6 w-full rounded-md px-5 py-2.5 font-semibold bg-zinc-800 text-white hover:bg-zinc-700 flex items-center justify-center gap-2">
                  {processing === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownCircle className="w-4 h-4" />} {id === "free" ? "Cancel subscription" : `Downgrade to ${p.name}`}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {current !== "free" && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6" data-testid="transfer-panel">
          <h3 className="font-head font-semibold flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-volt rotate-[-90deg]" /> Transfer this plan</h3>
          <p className="text-sm text-zinc-500 mt-1">Move this server's <span className="text-zinc-300 capitalize">{current}</span> plan to another server you own. This server will become Free.</p>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <select data-testid="transfer-select" value={transferTo} onChange={(e) => setTransferTo(e.target.value)} className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt min-w-[220px]">
              <option value="">Choose a destination server…</option>
              {myGuilds.filter((g) => g.id !== guildId && (g.plan || "free") === "free").map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <button data-testid="transfer-btn" onClick={doTransfer} disabled={!transferTo} className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-sm font-medium rounded-md px-4 py-2">Transfer plan</button>
          </div>
        </div>
      )}

      {maxSub?.active && (
        <div className="bg-zinc-900 border border-volt/40 rounded-xl p-6 mt-6" data-testid="max-sharing-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-head font-semibold flex items-center gap-2"><Server className="w-4 h-4 text-volt" /> Max — Shared Servers</h3>
              <p className="text-sm text-zinc-500 mt-1">Your Max subscription unlocks premium on up to {maxSub.slots_total} servers you own.</p>
            </div>
            <span className="text-sm font-mono bg-black/40 border border-zinc-800 rounded-full px-3 py-1" data-testid="max-slots">{maxSub.slots_used} / {maxSub.slots_total} used</span>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Linked servers</div>
              <div className="space-y-2">
                {maxSub.linked_guilds.map((g) => (
                  <div key={g.id} data-testid={`max-linked-${g.id}`} className="flex items-center justify-between bg-black/30 border border-zinc-800 rounded-md px-3 py-2">
                    <span className="text-sm text-zinc-200 flex items-center gap-2">{g.name} {g.is_primary && <span className="text-[10px] uppercase bg-volt text-volt-ink px-1.5 py-0.5 rounded font-bold">Primary</span>}</span>
                    {!g.is_primary && <button disabled={maxBusy} onClick={() => unlinkMax(g.id)} className="text-xs text-red-400 hover:text-red-300">Unlink</button>}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Add a server</div>
              <div className="space-y-2">
                {maxSub.available_guilds.length === 0 && <p className="text-sm text-zinc-600">No other servers available to link.</p>}
                {maxSub.available_guilds.map((g) => (
                  <div key={g.id} data-testid={`max-available-${g.id}`} className="flex items-center justify-between bg-black/30 border border-zinc-800 rounded-md px-3 py-2">
                    <span className="text-sm text-zinc-300">{g.name}</span>
                    <button disabled={maxBusy || maxSub.slots_used >= maxSub.slots_total} onClick={() => linkMax(g.id)} className="text-xs text-volt hover:text-volt-hover disabled:opacity-40 flex items-center gap-1"><Plus className="w-3 h-3" /> Link</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6 max-w-2xl">
          <h3 className="font-head font-semibold mb-4">Billing History</h3>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} data-testid="billing-history-row" className="flex items-center justify-between text-sm border-b border-zinc-800 pb-2 last:border-0">
                <span className="capitalize text-zinc-300">{h.type} · <span className="text-zinc-500">{h.plan_id}</span></span>
                <span className="font-mono text-zinc-400">{h.amount > 0 ? `£${h.amount.toFixed(2)}` : "—"} · {new Date(h.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-zinc-600 mt-6">Test mode — use card 4242 4242 4242 4242, any future expiry & CVC. Downgrades & cancellations take effect immediately.</p>
    </div>
  );
}
